import type { Role } from "@manos/core";

/*
 * The guided tour: one walk through the whole Studio, and short tours per screen made from the same
 * steps (the "?" on each screen replays that screen's). Steps point at real controls by CSS selector
 * (the first visible match); a step whose control isn't on screen is skipped, so the tour fits each
 * person's role and data. Nothing in the tour changes anything: it only points.
 */
export type Screen = "studio" | "works" | "work" | "media" | "pages" | "builder" | "people" | "inbox" | "account" | "admin";

/** Where a step is shown: a path, or "the first work / media item you can open" (resolved at run time). */
export type StepRoute = string | "first-work" | "first-media";

export type TourStep = {
  id: string;
  screen: Screen;
  route?: StepRoute;
  /** What to point at, in order of preference (the first that's on screen); none centres the card. */
  target?: string | string[];
  title: string;
  body: string;
  /** Only for these roles (default: everyone). */
  roles?: Role[];
};

const EDITORS: Role[] = ["admin", "editor"];

/** Each section's tour opens by showing where it lives: its sidebar link, or Menu on phones. */
const navStep = (screen: Screen, path: string, title: string, body: string, roles?: Role[]): TourStep => ({
  id: `${screen}-nav`,
  screen,
  route: path,
  target: [`.side .nav a[href="${path}"]`, ".menu-toggle"],
  title,
  body: `${body} On a phone, it's under Menu.`,
  roles,
});

export const STEPS: TourStep[] = [
  {
    id: "welcome",
    screen: "studio",
    title: "Welcome to Manos Studio",
    body: "A quick look around: where things are and how the team works together here. Use → and ← or the buttons; Esc ends the tour at any time.",
  },
  {
    id: "nav",
    screen: "studio",
    target: ".side .nav",
    title: "The sections",
    body: "Pages, Works, Media library, People and the Inbox. Counts here mean something new: unread comments, or messages.",
  },
  {
    id: "search",
    screen: "studio",
    target: ".cmd-open",
    title: "Search or jump",
    body: "Press ⌘K anywhere to open any work, person or page by name, or to start something new.",
  },
  {
    id: "bell",
    screen: "studio",
    target: "[data-tour=bell]",
    title: "Notifications",
    body: "Mentions, replies, client notes, review requests and more. Choose which ones also reach your email under your name → Your account.",
  },
  {
    id: "me",
    screen: "studio",
    target: ".me",
    title: "You",
    body: "Your account, your notification emails, signing out, and this tour again.",
  },
  {
    id: "collapse",
    screen: "studio",
    target: ".rail-toggle",
    title: "More room",
    body: "Fold the sidebar to icons (⌘\\) when you want space; it stays how you leave it.",
  },

  navStep("works", "/works", "Getting to Works", "Works is in the sidebar. To open one by name from anywhere, press ⌘K and type its title."),
  {
    id: "works-list",
    screen: "works",
    route: "/works",
    target: [".work-rows", ".content"],
    title: "All your works",
    body: "Every film you can see. A dot means unread comments, faces mean someone has it open now, and Private means only the people it's shared with can see it.",
  },
  { id: "works-filter", screen: "works", route: "/works", target: ".content .seg", title: "Find one", body: "Filter by status, or search by title or client." },
  {
    id: "works-categories",
    screen: "works",
    route: "/works",
    target: "[data-tour=categories]",
    title: "Categories",
    body: "How works are grouped on the site, in English and Spanish.",
    roles: EDITORS,
  },
  {
    id: "works-new",
    screen: "works",
    route: "/works",
    target: "[data-tour=new-work]",
    title: "New work",
    body: "Starts private to you. Share it when it's ready for others.",
  },

  {
    id: "work-title",
    screen: "work",
    route: "first-work",
    target: ".doc-title",
    title: "A work",
    body: "Everything saves as you type; the dot at the top is green when it's all saved. If someone else edits at the same time, their changes merge in.",
  },
  {
    id: "work-hero",
    screen: "work",
    route: "first-work",
    target: "[data-field=heroMediaId]",
    title: "The film",
    body: "Plays right here. Point along the bar to preview frames; J and L jump 10 seconds, comma and full stop step a frame.",
  },
  {
    id: "work-credits",
    screen: "work",
    route: "first-work",
    target: "[data-field=credits]",
    title: "Credits",
    body: "Who made it. Credits linked to a person also appear on their profile.",
  },
  {
    id: "work-share",
    screen: "work",
    route: "first-work",
    target: ".share-btn",
    title: "Share",
    body: "Who can see it, and what each person can do: view, comment or edit. Admins always can.",
  },
  {
    id: "work-comments",
    screen: "work",
    route: "first-work",
    target: ".comments-toggle",
    title: "Comments",
    body: "Talk about the cut, pinned to moments in the film. Type @ to mention someone; press C while it plays to comment at that moment.",
  },
  {
    id: "work-publish",
    screen: "work",
    route: "first-work",
    target: ".editor-top .topbar-actions .btn.primary",
    title: "Going live",
    body: "Editors publish; contributors submit for review. Visitors only ever see what's been published.",
  },
  {
    id: "work-links",
    screen: "work",
    route: "first-work",
    target: "[data-tour=client-links]",
    title: "Private client links",
    body: "Show a client the cut before it's live. Each link is for one person; they confirm who they are every visit, and their notes come back as comments.",
  },
  {
    id: "work-history",
    screen: "work",
    route: "first-work",
    target: "[data-tour=history]",
    title: "History",
    body: "Every version, kept. Restore an earlier one if you need to.",
  },

  navStep("media", "/media", "Getting to the Media library", "The Media library is in the sidebar: every film and still the team has uploaded."),
  {
    id: "media-upload",
    screen: "media",
    route: "/media",
    target: "[data-tour=upload]",
    title: "Uploading",
    body: "Upload films and stills of any size; uploads resume if the connection drops, and processing carries on without you.",
  },
  {
    id: "media-lib",
    screen: "media",
    route: "/media",
    target: [".lib", ".content"],
    title: "The library",
    body: "Point at a film to scrub through it. A padlock means it's private to the people it's shared with.",
  },
  {
    id: "media-focal",
    screen: "media",
    route: "first-media",
    target: ".focal",
    title: "Focal point",
    body: "Mark what must stay in frame; every crop on the site follows it. Recenter puts it back in the middle.",
  },
  {
    id: "media-share",
    screen: "media",
    route: "first-media",
    target: ".detail .share-btn",
    title: "Sharing media",
    body: "New uploads start private to you. Share them so others can find them and use them in works.",
  },

  navStep("pages", "/pages", "Getting to Pages", "Pages is in the sidebar: the pages of manosedit.com.", EDITORS),
  {
    id: "pages-list",
    screen: "pages",
    route: "/pages",
    target: ".rows",
    title: "Pages",
    body: "Each page is a layout of blocks. Edit the draft freely; visitors see it only after you publish.",
    roles: EDITORS,
  },
  {
    id: "pages-builtin",
    screen: "pages",
    route: "/pages",
    target: ".built-in",
    title: "Built-in pages",
    body: "Work, People and Contact: show or hide them on the site.",
    roles: ["admin"],
  },
  {
    id: "builder-canvas",
    screen: "builder",
    route: "/pages/home",
    target: ".canvas-area",
    title: "The page builder",
    body: "What you see is the site. Click a block to change it, drag to reorder, and check phone and tablet sizes up top.",
    roles: EDITORS,
  },
  {
    id: "builder-add",
    screen: "builder",
    route: "/pages/home",
    target: ".add-block",
    title: "Add a block",
    body: "Showcases, heroes, grids, reels, features, images and text.",
    roles: EDITORS,
  },

  navStep("people", "/people", "Getting to People", "People is in the sidebar."),
  {
    id: "people",
    screen: "people",
    route: "/people",
    target: [".people-grid", ".content"],
    title: "People",
    body: "Directors, photographers and artists. Each gets a profile that lists their work, built from the credits.",
  },
  navStep("inbox", "/inbox", "Getting to the Inbox", "The Inbox is in the sidebar; its count shows unread messages."),
  {
    id: "inbox",
    screen: "inbox",
    route: "/inbox",
    target: [".content .rows", ".content"],
    title: "Inbox",
    body: "Messages from the contact form on manosedit.com. Each one is also emailed to hello@.",
  },
  {
    id: "account",
    screen: "account",
    route: "/account",
    target: "#notifications",
    title: "Your notifications",
    body: "For each kind: email right away, gather into a daily summary at 8am Pacific, or leave it in the bell.",
  },
  {
    id: "admin",
    screen: "admin",
    route: "/admin/team",
    target: ".admin-tabs",
    title: "Admin",
    body: "Who can sign in, the services the Studio depends on, everything that's happened, and what to check if something seems off.",
    roles: ["admin"],
  },

  {
    id: "done",
    screen: "studio",
    title: "That's the Studio",
    body: "Press ? on any screen to replay just that screen's tour, or start this one again from your name → Take the tour.",
  },
];

/** Screens a role can reach; their steps (and their "?") are the only ones shown. */
const SCREEN_ROLES: Partial<Record<Screen, Role[]>> = { admin: ["admin"], pages: EDITORS, builder: EDITORS };

export const tourFor = (screen: Screen | "full", role: Role) =>
  STEPS.filter(
    (s) =>
      (screen === "full" || (s.screen === screen && s.id !== "welcome" && s.id !== "done")) &&
      (!s.roles || s.roles.includes(role)) &&
      (!SCREEN_ROLES[s.screen] || SCREEN_ROLES[s.screen]!.includes(role)),
  );

/** Which screen a path is, for the "?" button. */
export function screenOf(path: string): Screen | null {
  if (/^\/works\/[^/]+/.test(path)) return "work";
  if (path === "/works") return "works";
  if (path === "/media") return "media";
  if (/^\/pages\/[^/]+/.test(path)) return "builder";
  if (path === "/pages") return "pages";
  if (path.startsWith("/people")) return "people";
  if (path === "/inbox") return "inbox";
  if (path === "/account") return "account";
  if (path.startsWith("/admin")) return "admin";
  return null;
}

export const SCREEN_NAMES: Record<Screen, string> = {
  studio: "the Studio",
  works: "Works",
  work: "this work",
  media: "the Media library",
  pages: "Pages",
  builder: "the page builder",
  people: "People",
  inbox: "the Inbox",
  account: "your account",
  admin: "Admin",
};
